/**
 * Hosted UI login (R24.4).
 *
 * There is no in-SPA credential form anymore. Sign-in is a redirect to the
 * Cognito Hosted UI, which owns the whole ceremony — native email/password,
 * first-login password set, TOTP enrollment (QR), and social federation. This
 * screen just offers the entry points:
 *   - a primary "Sign in" button → the Hosted UI chooser (native + any social), and
 *   - optional per-provider buttons ("Continue with Google/Amazon") that jump
 *     straight to that provider, rendered only for providers the deploy wired
 *     (from runtime config `socialProviders`).
 *
 * Clicking any button navigates the browser away to the Hosted UI, so this
 * component does not track local auth state; on return, the bootstrap exchanges
 * the code and renders the app.
 */

import { useState } from "react";
import type { CognitoAuth } from "../auth.js";
import type { SocialProvider } from "../config.js";

export interface LoginScreenProps {
  readonly auth: CognitoAuth;
  /** Social providers the deploy enabled (from runtime config). */
  readonly socialProviders?: readonly SocialProvider[];
}

export function LoginScreen({ auth, socialProviders = [] }: LoginScreenProps) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const go = async (provider?: SocialProvider) => {
    setError(null);
    setBusy(true);
    try {
      await auth.redirectToLogin(provider);
      // On success the browser navigates away; if we're still here, the redirect
      // did not happen — surface it rather than leaving a dead button.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start sign-in.");
      setBusy(false);
    }
  };

  return (
    <section aria-label="Sign in">
      <h1>Sign in to Rufus</h1>
      {error ? <p role="alert">{error}</p> : null}

      <button type="button" disabled={busy} onClick={() => void go()}>
        Sign in
      </button>

      {socialProviders.map((provider) => (
        <button
          key={provider}
          type="button"
          disabled={busy}
          onClick={() => void go(provider)}
        >
          Continue with {provider}
        </button>
      ))}
    </section>
  );
}
